import { CommentDataType } from '../../shared/components/Comments/Comments.types'
import { getColumnWidth, TableColumnDefinition } from '../../shared/hooks/useTableColumnLayout'
import {
    CommentsViewColumnId,
    CommentsViewComment,
    CommentsViewDomainTabId,
    CommentsViewFiltersState,
    CommentsViewNavigationTarget,
} from './CommentsView.types'

export type CommentsViewTabLike = {
    id: string
    label: string
    getTypeLabel: (dataType: CommentDataType) => string
    getLocationLabel: (comment: CommentsViewComment) => string
    getLocationTarget: (comment: CommentsViewComment, currentPath: string) => CommentsViewNavigationTarget | null
}

export type CommentRowData = {
    comment: CommentsViewComment
    typeLabel: string
    locationLabel: string
    locationTarget: CommentsViewNavigationTarget | null
}

export type SortDirection = 'asc' | 'desc'

export type SortState = {
    columnId: CommentsViewColumnId
    direction: SortDirection
} | null

export const COMMENT_CELL_MAX_HEIGHT = 220

export const COMMENTS_VIEW_COLUMNS: TableColumnDefinition<CommentsViewColumnId>[] = [
    { id: 'user', label: 'User' },
    { id: 'comment', label: 'Comment', width: 315 },
    { id: 'type', label: 'Type' },
    { id: 'location', label: 'Location', width: 280 },
    { id: 'date', label: 'Created' },
    { id: 'updated', label: 'Updated' },
    { id: 'resolved', label: 'Resolved' },
    { id: 'action', label: 'Action', width: 300 },
]

export const STATIC_COMMENTS_COLUMN_ID: CommentsViewColumnId = 'comment'

/** Mirrors initialState.commentsViewFilters.hiddenColumnIds; CommentsView.helpers.test keeps them in step. */
export const DEFAULT_HIDDEN_COMMENTS_COLUMN_IDS: CommentsViewColumnId[] = ['updated']

export const HIDEABLE_COMMENTS_VIEW_COLUMNS = COMMENTS_VIEW_COLUMNS.filter(
    (column) => column.id !== STATIC_COMMENTS_COLUMN_ID
)

export const toHiddenCommentsColumnIds = (columnIds: readonly string[]): CommentsViewColumnId[] =>
    columnIds.filter((columnId): columnId is CommentsViewColumnId => columnId !== STATIC_COMMENTS_COLUMN_ID)

export const columnWidths = COMMENTS_VIEW_COLUMNS.reduce(
    (widths, column) => {
        widths[column.id] = getColumnWidth(column)

        return widths
    },
    {} as Record<CommentsViewColumnId, number>
)

export const INITIAL_FILTERS_STATE: CommentsViewFiltersState = {
    common: {},
    dateRange: {
        from: null,
        to: null,
    },
    domains: {},
}

export const normalizeCommentsViewFiltersState = (
    filtersState?: Partial<CommentsViewFiltersState> | null
): CommentsViewFiltersState => ({
    common: filtersState?.common ?? INITIAL_FILTERS_STATE.common,
    dateRange: {
        from: filtersState?.dateRange?.from ?? INITIAL_FILTERS_STATE.dateRange.from,
        to: filtersState?.dateRange?.to ?? INITIAL_FILTERS_STATE.dateRange.to,
    },
    domains: filtersState?.domains ?? INITIAL_FILTERS_STATE.domains,
})

export const normalizeAction = (value?: string) => value?.trim() ?? ''

export const formatCommentDate = (createdAt: string) =>
    new Date(createdAt).toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
    })

const normalizeSortValue = (value: string | number | boolean | null | undefined) => {
    if (typeof value === 'string') {
        return value.trim().toLocaleLowerCase()
    }

    if (typeof value === 'boolean') {
        return value ? 1 : 0
    }

    return value ?? ''
}

const compareSortValues = (
    left: string | number | boolean | null | undefined,
    right: string | number | boolean | null | undefined
) => {
    const normalizedLeft = normalizeSortValue(left)
    const normalizedRight = normalizeSortValue(right)

    if (typeof normalizedLeft === 'string' && typeof normalizedRight === 'string') {
        return normalizedLeft.localeCompare(normalizedRight, undefined, { sensitivity: 'base' })
    }

    if (normalizedLeft < normalizedRight) {
        return -1
    }

    if (normalizedLeft > normalizedRight) {
        return 1
    }

    return 0
}

export const sortCommentRows = (rows: CommentRowData[], sortState: SortState) => {
    if (!sortState) {
        return rows
    }

    const directionMultiplier = sortState.direction === 'asc' ? 1 : -1

    return [...rows].sort((left, right) => {
        const result = (() => {
            switch (sortState.columnId) {
                case 'user':
                    return compareSortValues(left.comment.user?.name ?? '', right.comment.user?.name ?? '')
                case 'comment':
                    return compareSortValues(left.comment.text, right.comment.text)
                case 'type':
                    return compareSortValues(left.typeLabel, right.typeLabel)
                case 'location':
                    return compareSortValues(left.locationLabel, right.locationLabel)
                case 'date':
                    return compareSortValues(
                        new Date(left.comment.createdAt).getTime(),
                        new Date(right.comment.createdAt).getTime()
                    )
                case 'updated':
                    return compareSortValues(
                        new Date(left.comment.updatedAt).getTime(),
                        new Date(right.comment.updatedAt).getTime()
                    )
                case 'resolved':
                    return compareSortValues(left.comment.isResolved, right.comment.isResolved)
                case 'action':
                    return compareSortValues(
                        normalizeAction(left.comment.action),
                        normalizeAction(right.comment.action)
                    )
                default:
                    return 0
            }
        })()

        return result * directionMultiplier
    })
}

const getNormalizedSearchQuery = (value: string) => value.trim().toLocaleLowerCase()

const getCommentsDashboardSearchText = (
    comment: CommentsViewComment,
    getTypeLabel: (dataType: CommentDataType) => string,
    getLocationLabel: (comment: CommentsViewComment) => string
) =>
    [
        comment.user?.name ?? '',
        comment.text,
        comment.action ?? '',
        getTypeLabel(comment.dataType),
        getLocationLabel(comment),
        formatCommentDate(comment.createdAt),
        comment.isResolved ? 'resolved' : 'unresolved',
    ]
        .join(' ')
        .toLocaleLowerCase()

export const applyCommentsViewSearch = (
    comments: CommentsViewComment[],
    searchQuery: string,
    getTypeLabel: (dataType: CommentDataType) => string,
    getLocationLabel: (comment: CommentsViewComment) => string
) => {
    const normalizedQuery = getNormalizedSearchQuery(searchQuery)

    if (!normalizedQuery) {
        return comments
    }

    return comments.filter((comment) =>
        getCommentsDashboardSearchText(comment, getTypeLabel, getLocationLabel).includes(normalizedQuery)
    )
}

const COMMENTS_CSV_HEADER_LABEL_BY_COLUMN_ID: Record<CommentsViewColumnId, string> = {
    user: 'User',
    comment: 'Comment',
    type: 'Type',
    location: 'Location',
    date: 'Date',
    updated: 'Updated',
    resolved: 'Resolved',
    action: 'Action',
}

const buildCsvValue = (value: string | number | boolean | null | undefined) => {
    const normalizedValue = value == null ? '' : String(value)

    if (!/[",\n]/.test(normalizedValue)) {
        return normalizedValue
    }

    return `"${normalizedValue.replace(/"/g, '""')}"`
}

export const getCommentsCsv = (
    comments: CommentsViewComment[],
    visibleColumnIds: CommentsViewColumnId[],
    getTypeLabel: (comment: CommentsViewComment) => string,
    getLocationLabel: (comment: CommentsViewComment) => string
) => {
    const getColumnValue = (comment: CommentsViewComment, columnId: CommentsViewColumnId) => {
        switch (columnId) {
            case 'user':
                return comment.user?.name ?? ''
            case 'comment':
                return comment.text
            case 'type':
                return getTypeLabel(comment)
            case 'location':
                return getLocationLabel(comment)
            case 'date':
                return formatCommentDate(comment.createdAt)
            case 'updated':
                return formatCommentDate(comment.updatedAt)
            case 'resolved':
                return comment.isResolved ? 'Yes' : 'No'
            case 'action':
                return comment.action ?? ''
            default:
                return ''
        }
    }

    const header = visibleColumnIds.map((columnId) => COMMENTS_CSV_HEADER_LABEL_BY_COLUMN_ID[columnId])
    const rows = comments.map((comment) => visibleColumnIds.map((columnId) => getColumnValue(comment, columnId)))

    return [header, ...rows].map((row) => row.map(buildCsvValue).join(',')).join('\n')
}

export const downloadCommentsCsv = (filename: string, csvContent: string) => {
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')

    link.href = url
    link.download = filename
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)

    URL.revokeObjectURL(url)
}

export const getDefaultCommentsCsvFilename = (strategyTitle: string, tabLabel: string, isFiltered = false) => {
    const sanitizedStrategyTitle = strategyTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-')
    const sanitizedTabLabel = tabLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-')
    const filteredSuffix = isFiltered ? '-filtered' : ''

    return `${sanitizedStrategyTitle}-${sanitizedTabLabel}-comments${filteredSuffix}.csv`
}

export const getCommentsCsvBaseName = (filename: string) => filename.replace(/\.csv$/i, '')

export const getNormalizedCommentsCsvFilename = (value: string, fallbackFilename: string) => {
    const fallbackBaseName = fallbackFilename.replace(/\.csv$/i, '')
    const normalizedBaseName = value
        .trim()
        .replace(/\.csv$/i, '')
        .replace(/[<>:"/\\|?*\u0000-\u001F]/g, '')
        .replace(/\s+/g, ' ')
        .trim()

    return `${normalizedBaseName || fallbackBaseName}.csv`
}

export const getCommentsTabForDataType = (
    dataType: CommentDataType,
    tabsById: Partial<Record<CommentsViewDomainTabId, CommentsViewTabLike>>
) => {
    switch (dataType) {
        case 'strategicImperative':
        case 'strategicImperativeTable':
            return tabsById.strategicImperative
        case 'valueMessage':
        case 'valueMessageTable':
        case 'valueNarrative':
            return tabsById.valueMessage
        case 'activity':
        case 'activityTable':
            return tabsById.activity
        case 'evidence':
        case 'evidenceTable':
            return tabsById.evidence
        case 'tppCell':
            return tabsById.tpp
        default:
            return undefined
    }
}

export const getCommentsTabForComment = (
    comment: Pick<CommentsViewComment, 'dataType'> & Partial<Pick<CommentsViewComment, 'location'>>,
    tabsById: Partial<Record<CommentsViewDomainTabId, CommentsViewTabLike>>
) => {
    if (comment.location?.domain && tabsById[comment.location.domain]) {
        return tabsById[comment.location.domain]
    }

    return getCommentsTabForDataType(comment.dataType, tabsById)
}
